import React, { useState } from "react";
import "../styles/ViewOnce.css";

function hasUserViewed(message, userId) {
  const uid = String(userId);
  return (message?.viewedBy || []).some((entry) => String(entry.userId) === uid);
}

function mediaLabel(message) {
  const type = message?.messageType || message?.type;
  if (type === "video") return "Video";
  if (type === "audio") return "Audio";
  return "Photo";
}

function ViewOnceViewer({ type, src, onClose }) {
  return (
    <div className="view-once-viewer-overlay" onClick={onClose}>
      <div className="view-once-viewer" onClick={(event) => event.stopPropagation()}>
        <div className="view-once-viewer-header">
          <span>View once</span>
          <button type="button" className="view-once-viewer-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="view-once-viewer-body">
          {type === "video" ? (
            <video src={src} controls autoPlay playsInline className="view-once-player" />
          ) : type === "audio" ? (
            <audio src={src} controls autoPlay className="view-once-player" />
          ) : (
            <img src={src} alt="View once" className="view-once-image" />
          )}
        </div>
      </div>
    </div>
  );
}

function ViewOnceMedia({
  message,
  currentUserId,
  isSender,
  endpoint,
  onViewed,
}) {
  const [opening, setOpening] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [consumedHere, setConsumedHere] = useState(false);
  const viewedByMe =
    !isSender &&
    (consumedHere ||
      message.viewOnceViewed ||
      hasUserViewed(message, currentUserId));
  const openedByAnyone = (message.viewedBy || []).length > 0 || consumedHere;
  const type = message.messageType || message.type || "image";
  const showAlreadyViewed = viewedByMe && !viewer && !opening;

  const closeViewer = () => {
    if (viewer?.src) {
      URL.revokeObjectURL(viewer.src);
    }
    setViewer(null);
    if (!isSender) {
      setConsumedHere(true);
      if (onViewed) onViewed(message._id);
    }
  };

  const openMedia = async () => {
    if (opening || viewer) return;
    if (!isSender && viewedByMe) return;
    setOpening(true);
    try {
      const response = await fetch(endpoint);
      if (response.status === 403) {
        setConsumedHere(true);
        if (onViewed) onViewed(message._id);
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to open media");
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0 || (blob.type || "").includes("application/json")) {
        throw new Error("Failed to open media");
      }
      const src = URL.createObjectURL(blob);
      setViewer({ type, src });
      if (!isSender) {
        setConsumedHere(true);
      }
    } catch (error) {
      console.error("Error opening view once media:", error);
      alert(error.message || "Failed to open media");
    } finally {
      setOpening(false);
    }
  };

  return (
    <>
      {showAlreadyViewed ? (
        <div className="view-once-card viewed">
          <span className="view-once-badge">①</span>
          <div>
            <strong>Already Viewed</strong>
            <span>{mediaLabel(message)}</span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`view-once-card${isSender && openedByAnyone ? " opened" : ""}`}
          onClick={openMedia}
          disabled={opening}
        >
          <span className="view-once-badge">①</span>
          <div>
            <strong>
              {opening
                ? "Opening..."
                : isSender && openedByAnyone
                  ? "Opened"
                  : "View Once"}
            </strong>
            <span>{mediaLabel(message)}</span>
          </div>
        </button>
      )}
      {viewer && (
        <ViewOnceViewer type={viewer.type} src={viewer.src} onClose={closeViewer} />
      )}
    </>
  );
}

export default ViewOnceMedia;
